/**
 * SettingsToolsPill — «один поповер инструментов» (Artlist-style).
 *
 * Summary-пилюля (≋ «16:9 / JPG / Среднее») + стеклянный поповер, внутри
 * которого SettingsPillsRow в section-режиме (BbarSectionMode): каждая
 * пилюля-инструмент рендерится секцией с чипами/тумблером/слайдером.
 * Инструменты и их логика НЕ дублируются — единственный источник правды
 * остаётся SettingsPillsRow.
 *
 * Используется в двух барах (единый вид, ноль дрейфа):
 *   1. GenerateInputBar (.gm__bottom) — классический бар виджета/страницы;
 *   2. GenerateToolMode (.gtm__bar) — фокус-режим инструмента карточки.
 *
 * Одно-пилюльные ветки (tts / sounds / soundtrack / HeyGen) НЕ группируются
 * (единственный инструмент прятать за клик бессмысленно) — рендерится
 * обычный SettingsPillsRow. Презентации ГРУППИРУЮТСЯ (2026-08-13): у Gamma
 * девять контролов — строки-пикеры Темы/Аудитории/Инструкций живут в
 * PresentationPills (section-режим).
 *
 * Мобила (≤768): вместо десктоп-поповера та же пилюля открывает штатную
 * ШТОРКУ настроек MobileSettingsSheet (универсальный Sheet — как всё
 * мобильное в проекте). Без селекта модели: в tool-режиме модель фиксирована
 * карточкой (смена = авто-детач), а классический бар на мобиле рендерит
 * собственную кнопку с этой же шторкой (.gm__mobile-model-btn).
 */
import { useState } from 'react'
import { useGenerate } from '../../../context/GenerateContext'
import { useMediaQuery } from '../../../hooks/useMediaQuery'
import { getHeyGenModelKind } from '../../../api/heygen'
import { PROVIDER } from '../utils/constants'
import { modelParams, inputsAttachedOf, effectiveOutputFormat, outputFormatOptions } from '../utils/modelParams'
import { switchSeedanceMode } from '../utils/seedanceMode'
import { switchVideoIntent } from '../utils/videoIntent'
import {
	ASPECT_FOLLOWS_LABEL,
	VIDEO_INTENT_OPTIONS,
	aspectFollowsReference,
	durationLock,
	ownReferenceVideos,
	trendVideoAspectLabel,
	trendVideoFollows,
	videoIntentOf,
} from '../utils/referenceLimits'

import SettingsPillsRow from './SettingsPillsRow'
import BottomBarPopover from './BottomBarPopover'
import MobileSettingsSheet from './MobileSettingsSheet'
import { BbarSectionMode } from './bbarSectionContext'
import { IconSliders } from './WidgetModelPicker'
import {
	GAMMA_IMAGE_MODEL_OPTIONS,
	GAMMA_IMAGE_SOURCE_OPTIONS,
	GAMMA_LANGUAGE_OPTIONS,
	GAMMA_TEXT_AMOUNT_OPTIONS,
	GAMMA_TEXT_MODE_OPTIONS,
	GAMMA_TONE_OPTIONS,
} from '../utils/gammaOptions'
import { hfSummaryParts } from '../utils/gammaHf'
import { qualityLabelShort } from '../utils/qualityLabels'
import { clipSummaryParts } from '../motion/motionTool'

export default function SettingsToolsPill() {
	const gen = useGenerate()
	const isMobile = useMediaQuery('(max-width: 768px)')
	const [sheetOpen, setSheetOpen] = useState(false)
	const { selectedModel: model, settings, isAudio, isPresentation } = gen

	const heyKind = model ? getHeyGenModelKind(model.backendModel) : null
	// Презентации ГРУППИРУЮТСЯ (решение владельца 2026-08-13): у Gamma девять
	// контролов — ряд пилюль разросся, единая панель настроек уместнее.
	const groupTools =
		!!model &&
		!heyKind &&
		!(isAudio && ['sounds', 'tts', 'soundtrack'].includes(settings.audioSubmode ?? ''))

	if (!groupTools) return <SettingsPillsRow />

	// Набор параметров — из общей спеки, той же, по которой рисуются пилюли и
	// строки шторки. Сводка перечисляет РОВНО то, что человек увидит в панели:
	// когда условия переписывались здесь заново, строка обещала параметр,
	// которого в панели нет (и наоборот).
	const p = modelParams(model, {
		referenceVideoUrl: gen.referenceVideo?.url,
		photoSlotsEngaged: gen.photoSlotsEngaged,
		seedanceMode: settings.seedanceMode,
		ownReferenceVideos: ownReferenceVideos(gen.referenceVideos).length,
		inputsAttached: inputsAttachedOf(gen),
		draftAvailable: gen.draftAvailable,
	})
	const draftOn = p.draft && settings.draft === true
	// Замки при видео-референсе Seedance 2.5: сводка показывает то, что спишется и выйдет.
	const followSec = durationLock(model, settings, gen.referenceVideos ?? [], !!gen.uploadedVideo?.url)?.sec ?? null
	const aspectLocked = aspectFollowsReference(model, settings, gen.referenceVideos ?? [])
	// Тренд переделывает своё видео (shared/trend-video): формат — «как у видео 16:9», длина — длина видео (followSec).
	const trendVideo = trendVideoFollows(model, settings, gen.referenceVideos ?? [])

	// Summary — ВСЕ выбранные параметры, как у Artlist
	// («16:9 / 480p / 4 Sec», «Auto Duration / Auto Genre / Auto Mood»).
	const parts: string[] = []
	// Режим Seedance (Кадры/Референсы) — первым, как пилюля в SettingsPillsRow.
	if (p.seedanceMode) parts.push(settings.seedanceMode === 'references' ? 'Референсы' : 'Кадры')
	if (p.videoIntent) parts.push(VIDEO_INTENT_OPTIONS.find((o) => o.value === videoIntentOf(settings))?.label ?? '')
	if (p.aspectRatio && settings.aspectRatio)
		parts.push(
			trendVideo
				? trendVideoAspectLabel(settings)
				: aspectLocked
					? ASPECT_FOLLOWS_LABEL
					: settings.aspectRatio === 'match_input_image'
						? 'Авто'
						: settings.aspectRatio,
		)
	// Нейросети, где формат и длину задаёт ролик (Motion Control, замена человека в ролике — режим «Повторить движение»):
	// «Как у видео · 720p · 8 с · Звук» — ровно те пилюли, что в панели (разрешение, обрезка ролика, его звук).
	const clipDriven = p.motionControl || p.videoReplace
	if (clipDriven)
		parts.push(...clipSummaryParts({
			resolution: p.resolution ? settings.resolution : null,
			seconds: gen.uploadedVideo?.url ? gen.selectedVideoDuration : null,
			sound: settings.keepOriginalSound,
		}))
	else if (p.resolution && settings.resolution) parts.push(draftOn ? '480p' : settings.resolution)
	if ((p.duration || p.durationSlider) && settings.duration) parts.push(`${followSec ?? settings.duration} сек`)
	if (p.outputFormat) {
		// Сводка пишет тот же формат, что пилюля внутри и запрос (эффективный для модели), а не сырое значение.
		const fmt = effectiveOutputFormat(model, settings.outputFormat)
		if (fmt) parts.push(outputFormatOptions(model).find((o) => o.value === fmt)?.label ?? fmt.toUpperCase())
	}
	if (p.quality && settings.quality)
		parts.push(
			qualityLabelShort(settings.quality),
		)
	// Включённые тумблеры тоже видны в сводке (юзер включил звук — строка
	// обязана это показать).
	if ((p.sound || p.generateAudio) && settings.sound) parts.push('Звук')
	if (p.webSearch && settings.webSearch) parts.push('Web')
	if (draftOn) parts.push('Черновик')
	if (isAudio) {
		parts.push(settings.audioStyle?.trim() || 'Авто стиль')
		parts.push(
			settings.audioInstrumental
				? 'Инструментал'
				: settings.audioVocalGender === 'm'
					? 'Муж. вокал'
					: settings.audioVocalGender === 'f'
						? 'Жен. вокал'
						: 'Авто вокал',
		)
		if (settings.audioCustomMode) parts.push('Кастомный')
	}
	// Презентации: сводка в порядке секций панели. Заполненные текстовые поля
	// и выбранная тема видны словом — принцип «выбрал — строка показывает».
	const pres = settings.presentation
	if (isPresentation) {
		if ((pres?.mode ?? 'create') === 'create') {
			if (pres?.gammaThemeId) parts.push(pres.gammaThemeName || 'Тема')
			const n = pres?.gammaNumCards ?? 5
			parts.push(`${n} слайд${n === 1 ? '' : n >= 2 && n <= 4 ? 'а' : 'ов'}`)
			const lang = pres?.gammaLanguage ?? 'ru'
			parts.push(GAMMA_LANGUAGE_OPTIONS.find((o) => o.value === lang)?.label || lang)
			if (pres?.gammaTextMode && pres.gammaTextMode !== 'generate')
				parts.push(
					GAMMA_TEXT_MODE_OPTIONS.find((o) => o.value === pres.gammaTextMode)?.label ||
						pres.gammaTextMode,
				)
			const src = pres?.gammaImageSource ?? 'aiGenerated'
			parts.push(GAMMA_IMAGE_SOURCE_OPTIONS.find((o) => o.value === src)?.label || src)
			if (src === 'aiGenerated' && pres?.gammaImageModel)
				parts.push(
					GAMMA_IMAGE_MODEL_OPTIONS.find((o) => o.value === pres.gammaImageModel)?.label ||
						pres.gammaImageModel,
				)
			parts.push((pres?.gammaDimensions ?? '16x9').replace('x', ':'))
			const amount = pres?.gammaTextAmount ?? 'medium'
			parts.push(GAMMA_TEXT_AMOUNT_OPTIONS.find((o) => o.value === amount)?.label || amount)
			if (pres?.gammaTone)
				parts.push(GAMMA_TONE_OPTIONS.find((o) => o.value === pres.gammaTone)?.label || 'Тон')
			if (hfSummaryParts(pres).length > 0) parts.push('Колонтитулы')
			if (pres?.gammaExportAs && pres.gammaExportAs !== 'pptx')
				parts.push(pres.gammaExportAs.toUpperCase())
			if (pres?.gammaAudience) parts.push('Аудитория')
			if (pres?.gammaAdditionalInstructions) parts.push('Инструкции')
		} else {
			const imgs = pres?.imageUrls?.length ?? 0
			parts.push(imgs > 0 ? `Картинки ${imgs}/5` : 'Правка PPTX')
		}
	}
	// Есть ли у модели ХОТЬ ОДИН настраиваемый параметр. Условия — те же, что
	// рисуют строки в SettingsPillsRow: если ни одно не сработает, панель
	// откроется пустой, и «Настройки» превращаются в кнопку в никуда.
	// Живой случай — Face Swap: у неё нет ни формата, ни разрешения, ни
	// качества, а бэк вдобавок стирает всё, кроме двух картинок (`_face_swap`
	// делает data.clear()). Условие универсальное: любая такая модель спрячет
	// пустой контрол сама.
	// Спрашиваем про МОДЕЛЬ, а не про текущий запрос: набор без контекста
	// (без видео-референса и слотов карточки) — иначе кнопка исчезала бы от
	// приложенного видео. NSFW и Midjourney остаются отдельно: их пилюли
	// живут по своим условиям (NSFW скрыт, у Midjourney — только t2i).
	const modelSettings = modelParams(model)
	const hasAnySetting =
		!!model &&
		(Object.values(modelSettings).some(Boolean) ||
			isAudio ||
			isPresentation ||
			model.supportsNsfwChecker === true ||
			model.provider === PROVIDER.MIDJOURNEY)

	// Без обрезки: юзер должен видеть ВСЕ выбранные параметры (обрезка прятала
	// только что включённые тумблеры — «свойство не добавилось»). На мобиле
	// длинную сводку клэмпит CSS (max-width + ellipsis у .bbar-pill__label).
	const summary = parts.join(' / ') || 'Настройки'

	// Настраивать нечего — контрол не рисуем вовсе. Кнопка, открывающая пустую
	// панель, читается как «настройки сломались», а не «их нет».
	if (!hasAnySetting) return null

	// Мобила: та же пилюля (toggle-режим BottomBarPopover — без panel клик
	// уходит в onToggle), но открывает штатную шторку настроек.
	if (isMobile) {
		return (
			<>
				<BottomBarPopover
					className="gm__tools-pill"
					icon={<IconSliders />}
					label={summary}
					tooltip="Настройки генерации"
					active={sheetOpen}
					onToggle={() => setSheetOpen(true)}
				/>
				<MobileSettingsSheet
					open={sheetOpen}
					onClose={() => setSheetOpen(false)}
					model={model}
					settings={settings}
					isLoading={gen.isLoading}
					selectedVideoDuration={gen.selectedVideoDuration}
					uploadedVideoDuration={gen.uploadedVideo?.duration}
					referenceVideoUrl={gen.referenceVideo?.url}
					negativePromptOpen={gen.negativePromptOpen}
					onSettingChange={gen.userUpdateSetting}
					onVideoDurationChange={gen.setSelectedVideoDuration}
					onNegativePromptToggle={() => gen.setNegativePromptOpen(!gen.negativePromptOpen)}
					photoSlotsEngaged={gen.photoSlotsEngaged}
					onSeedanceModeChange={(mode) => switchSeedanceMode({ settings: gen.settings, updateSetting: gen.userUpdateSetting, setUploadedImage: gen.setUploadedImage, setEndImage: gen.setEndImage, setReferenceImages: gen.setReferenceImages, setReferenceVideos: gen.setReferenceVideos, setReferenceVideo: gen.setReferenceVideo }, mode)}
					referenceVideos={gen.referenceVideos}
					inputsAttached={inputsAttachedOf(gen)}
					draftAvailable={gen.draftAvailable}
					onVideoIntentChange={(intent) => switchVideoIntent({ model, settings: gen.settings, referenceVideos: gen.referenceVideos ?? [], updateSetting: gen.userUpdateSetting }, intent)}
					audioOperation={gen.audioOperation}
					sourceTrackModel={gen.sourceTrack?.model ?? null}
				/>
			</>
		)
	}

	return (
		<BottomBarPopover
			className="gm__tools-pill"
			icon={<IconSliders />}
			label={summary}
			tooltip="Настройки генерации"
			width={300}
			panel={
				<BbarSectionMode.Provider value={true}>
					<div className="gm__tools-panel">
						<SettingsPillsRow />
					</div>
				</BbarSectionMode.Provider>
			}
		/>
	)
}
